/**
 * BackButton — jednotné „zpět" pro horní bary stránek a detailů (LHUI).
 * Konvence: ArrowLeft size 15, text-xs; breadcrumb s čitelným ink ve všech themes.
 */
import type { ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';

export interface BackButtonProps {
  /** Cíl navigace — renderuje se <Link>. Bez `to` se použije onClick (button). */
  to?: string;
  onClick?: () => void;
  /** Label tlačítka; default i18n common.back („Zpět") */
  label?: ReactNode;
  /** Aktuální položka za lomítkem — zapne breadcrumb režim */
  crumb?: ReactNode;
  className?: string;
}

const baseCls =
  'inline-flex items-center gap-1.5 text-xs font-medium text-gray-400 ' +
  'hover:text-gray-100 transition-colors';

export function BackButton({ to, onClick, label, crumb, className }: BackButtonProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const content = (
    <>
      <ArrowLeft size={15} className='shrink-0' />
      {label ?? t('common.back', 'Zpět')}
    </>
  );

  const btn = to
    ? <Link to={to} className={cn(baseCls, className)}>{content}</Link>
    : (
      <button
        type='button'
        onClick={onClick ?? (() => navigate(-1))}
        className={cn(baseCls, className)}
      >
        {content}
      </button>
    );

  if (crumb === undefined) return btn;

  return (
    <span className='inline-flex items-center gap-2 min-w-0'>
      {btn}
      <span className='text-gray-600 select-none' aria-hidden>/</span>
      <span className='text-sm font-semibold text-gray-100 truncate'>{crumb}</span>
    </span>
  );
}
